import React, { useMemo, useState } from 'react';
import { leadImportService } from '../../services/leadImportService';

type Mode = 'csv' | 'json' | 'ai_output';

interface LeadImportWizardProps {
  onClose: () => void;
  onImported: (batchId: string) => void;
}

const LeadImportWizard: React.FC<LeadImportWizardProps> = ({ onClose, onImported }) => {
  const [mode, setMode] = useState<Mode>('csv');
  const [batchName, setBatchName] = useState('');
  const [sourceType, setSourceType] = useState('manual_paste');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [textPayload, setTextPayload] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const title = useMemo(() => {
    if (mode === 'csv') return 'Upload CSV';
    if (mode === 'json') return 'Paste JSON';
    return 'Paste AI Output';
  }, [mode]);

  const handleParsePreview = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await leadImportService.parsePreview({
        format: mode === 'ai_output' ? 'ai_output' : 'json',
        payload: textPayload,
      });
      setPreviewCount(result.count);
    } catch (err: any) {
      setError(err?.message || 'Failed to parse payload.');
      setPreviewCount(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      if (mode === 'csv') {
        if (!csvFile) throw new Error('Select a CSV file first.');
        const form = new FormData();
        form.append('file', csvFile);
        form.append('batch_name', batchName || `CSV Import ${new Date().toISOString().slice(0, 10)}`);
        form.append('source_type', sourceType || 'csv');
        const batch = await leadImportService.createFromCsv(form);
        onImported(batch.id);
        return;
      }

      const parsed = await leadImportService.parsePreview({
        format: mode === 'ai_output' ? 'ai_output' : 'json',
        payload: textPayload,
      });
      const leads = parsed.preview.map((item) => (item.raw_data || {}) as Record<string, unknown>);
      const batch = await leadImportService.create({
        batch_name: batchName || `Lead Import ${new Date().toISOString().slice(0, 10)}`,
        source_type: sourceType || (mode === 'json' ? 'json' : 'manual_paste'),
        leads,
      });
      onImported(batch.id);
    } catch (err: any) {
      setError(err?.message || 'Import failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--color-bg-overlay)] p-4">
      <div className="w-full max-w-3xl rounded-lg border border-border bg-surface p-6 shadow-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-fg">Lead Import Wizard</h2>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="mb-4 flex gap-2">
          {(['csv', 'json', 'ai_output'] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded-md px-3 py-2 text-sm ${mode === m ? 'bg-primary text-white' : 'bg-muted text-fg'}`}
            >
              {m === 'ai_output' ? 'AI Output' : m.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm text-fg-secondary">Batch name</label>
            <input className="input-field w-full" value={batchName} onChange={(e) => setBatchName(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm text-fg-secondary">Source type</label>
            <input className="input-field w-full" value={sourceType} onChange={(e) => setSourceType(e.target.value)} />
          </div>

          {mode === 'csv' ? (
            <div>
              <label className="mb-1 block text-sm text-fg-secondary">{title}</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                className="input-field w-full"
              />
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-sm text-fg-secondary">{title}</label>
              <textarea
                className="input-field min-h-[220px] w-full"
                value={textPayload}
                onChange={(e) => setTextPayload(e.target.value)}
                placeholder={mode === 'json' ? '[{"company_name":"Acme","email":"sales@acme.com"}]' : 'Paste raw AI output with leads'}
              />
            </div>
          )}
        </div>

        {previewCount !== null && <p className="mt-3 text-sm text-fg-secondary">Preview parsed records: {previewCount}</p>}
        {error && <p className="mt-3 text-sm text-danger">{error}</p>}

        <div className="mt-6 flex justify-end gap-2">
          {mode !== 'csv' && (
            <button type="button" className="btn-secondary" onClick={handleParsePreview} disabled={loading}>
              Parse Preview
            </button>
          )}
          <button type="button" className="btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadImportWizard;
